<template>
  <div class="logger-sensor-manage">
    <div class="page-header">
      <div>
        <div class="page-title">로거/센서 설정</div>
        <div class="page-subtitle">
          현장에 배치된 로거를 기준으로 센서 등록, 센서종류, 설정값, 계산식을 관리합니다.
        </div>
      </div>

      <div class="header-actions">
        <select v-model="selectedSiteId" class="control site-select" @change="onSiteChange">
          <option :value="null">현장 선택</option>
          <option v-for="site in siteList" :key="site.id" :value="site.id">
            {{ site.siteName || site.name }}
          </option>
        </select>
      </div>
    </div>

    <div class="manage-grid">
      <div class="panel logger-panel">
        <div class="panel-title">등록된 로거 목록</div>
        <div class="panel-subtitle">라인번호</div>

        <div class="list-box">
          <button
            v-for="logger in loggers"
            :key="logger.localKey"
            class="logger-item"
            :class="{ active: selectedLoggerKey === logger.localKey }"
            @click="selectLogger(logger)"
          >
            <div class="logger-name">{{ logger.name || logger.loggerType || '-' }}</div>
            <div class="logger-meta">{{ logger.loggerLineNumber || '-' }}</div>
          </button>

          <div v-if="!loggers.length" class="empty-text">
            현장에 배치된 로거가 없습니다.
          </div>
        </div>
      </div>

      <div class="panel raw-panel">
        <div class="panel-title">
          선택한 로거
          <span v-if="selectedLogger">: {{ selectedLogger.name || selectedLogger.loggerType }}</span>
        </div>

        <div v-if="selectedLogger?.isMultiSensorLogger" class="multi-warning">
          다단센서 로거입니다.<br />
          TS / TN / R 계열은 <b>다단센서 관리</b> 메뉴에서 설정하세요.
        </div>

        <div v-else class="raw-table-wrapper">
          <table
            v-if="!loadingLoggerSensor"
            class="sensor-table"
          >
            <thead>
              <tr>
                <th>칼럼명</th>
                <th>설정 센서명</th>
                <th>상태</th>
                <th>작업</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="sensor in availableSensors"
                :key="sensor.sensorId"
                :class="{ active: selectedRawSensorId === sensor.sensorId }"
                @click="selectRawSensor(sensor)"
              >
                <td>{{ sensor.rawMeasureKey || sensor.sensorCode || sensor.name }}</td>
                <td>{{ resolveRegisteredName(sensor) }}</td>
                <td>
                  <span class="status-badge" :class="isRegistered(sensor) ? 'active' : 'inactive'">
                    {{ isRegistered(sensor) ? '등록' : '미등록' }}
                  </span>
                </td>
                <td>
                  <button
                    v-if="isRegistered(sensor)"
                    class="btn xs secondary"
                    @click.stop="selectRegisteredSensor(findRegisteredByMasterSensorId(sensor.sensorId))"
                  >
                    선택
                  </button>

                  <button
                    v-else
                    class="btn xs primary"
                    @click.stop="openRegisterModal(sensor)"
                  >
                    등록
                  </button>
                </td>
              </tr>

              <tr v-if="!availableSensors.length">
                <td colspan="4" class="empty-cell">
                  로거의 센서 원천 컬럼이 없습니다.
                </td>
              </tr>
            </tbody>
          </table>

          <div
            v-else
            class="loading-empty"
          >
            로거 센서 정보를 불러오는 중입니다.
          </div>
        </div>
      </div>

      <div class="panel registered-panel">
        <div class="panel-title">현장 등록 센서 목록</div>

        <div class="list-box">
          <button
            v-for="sensor in registeredSensors"
            :key="sensor.localKey"
            class="registered-item"
            :class="{ active: selectedRegisteredKey === sensor.localKey }"
            @click="selectRegisteredSensor(sensor)"
          >
            <div>
              <div class="registered-name">{{ sensor.name || sensor.sensorCode || '-' }}</div>
              <div class="registered-meta">
                {{ sensor.sensorTypeName || '센서종류 미설정' }}
              </div>
            </div>

            <button class="remove-btn" @click.stop="removeRegisteredSensor(sensor)">
              해제
            </button>
          </button>

          <div v-if="!registeredSensors.length" class="empty-text">
            등록된 센서가 없습니다.
          </div>
        </div>
      </div>

      <div class="panel setting-panel">
        <div class="panel-title">
          {{ selectedRegisteredSensor ? '센서 설정' : '센서를 선택해주세요' }}
        </div>

        <div v-if="selectedRegisteredSensor" class="setting-form">
          <div class="setting-row two">
            <div class="setting-item">
              <label>센서코드</label>
              <input v-model="selectedRegisteredSensor.sensorCode" class="control" />
            </div>

            <div class="setting-item">
              <label>시리얼번호</label>
              <input v-model="selectedRegisteredSensor.serialNumber" class="control" />
            </div>
          </div>

          <div class="setting-row two">
            <div class="setting-item">
              <label>센서명</label>
              <input v-model="selectedRegisteredSensor.name" class="control" />
            </div>

            <div class="setting-item">
              <label>소수점자리</label>
              <input v-model="selectedRegisteredSensor.decimalPlaces" class="control" type="number" />
            </div>
          </div>

          <div class="setting-row">
            <div class="setting-item">
              <label>센서종류</label>
              <select
                v-model="selectedRegisteredSensor.sensorTypeId"
                class="control"
                @change="onSensorTypeChange(selectedRegisteredSensor)"
              >
                <option :value="null">- 선택 -</option>
                <option
                  v-for="type in sensorTypeOptions"
                  :key="type.id"
                  :value="type.id"
                >
                  {{ type.name }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="selectedRegisteredSensor.sensorTypeId" class="setting-row two">
            <div class="setting-item">
              <label>설치일</label>
              <input
                v-model="selectedRegisteredSensor.configValues.install_date"
                class="control"
                type="date"
              />
            </div>

            <div class="setting-item">
              <label>단위</label>
              <input
                v-model="selectedRegisteredSensor.configValues.unit"
                class="control"
              />
            </div>
          </div>

          <template v-for="field in selectedSensorCustomFields" :key="field.id">
            <div class="setting-row" :class="{ two: !isFullWidthField(field) }">
              <div class="setting-item">
                <label v-if="isThresholdValueField(field)" class="threshold-label">
                  <input
                    type="checkbox"
                    :checked="isThresholdEnabled(selectedRegisteredSensor, getFieldKey(field))"
                    @change="setThresholdEnabled(selectedRegisteredSensor, getFieldKey(field), $event.target.checked)"
                  />
                  {{ field.fieldName || field.field_name }}
                </label>

                <label v-else>
                  {{ field.fieldName || field.field_name }}
                </label>

                <input
                  v-if="resolveInputType(field) === 'NUMBER'"
                  v-model="selectedRegisteredSensor.configValues[getFieldKey(field)]"
                  class="control"
                  type="number"
                  step="any"
                />

                <input
                  v-else-if="resolveInputType(field) === 'DATE'"
                  v-model="selectedRegisteredSensor.configValues[getFieldKey(field)]"
                  class="control"
                  type="date"
                />

                <select
                  v-else-if="resolveInputType(field) === 'SELECT_AXIS'"
                  v-model="selectedRegisteredSensor.configValues[getFieldKey(field)]"
                  class="control"
                >
                  <option value="">선택</option>
                  <option value="X">X축</option>
                  <option value="Y">Y축</option>
                  <option value="Z">Z축</option>
                </select>

                <select
                  v-else-if="resolveInputType(field) === 'SELECT_SENSOR'"
                  v-model="selectedRegisteredSensor.configValues[getFieldKey(field)]"
                  class="control"
                >
                  <option value="">선택</option>
                  <option
                    v-for="sensorItem in availableAxisSensors"
                    :key="sensorItem.sensorId"
                    :value="sensorItem.sensorId"
                  >
                    {{ sensorItem.name }} - {{ sensorItem.sensorCode || sensorItem.rawMeasureKey || '' }}
                  </option>
                </select>

                <input
                  v-else
                  v-model="selectedRegisteredSensor.configValues[getFieldKey(field)]"
                  class="control"
                />
              </div>
            </div>
          </template>
        </div>

        <div v-else class="empty-text setting-empty">
          좌측에서 등록된 센서를 선택하면 설정값을 수정할 수 있습니다.
        </div>
      </div>
    </div>

    <div class="bottom-action-bar">
      <button class="btn secondary bottom-action-btn" @click="reloadAll">
        새로고침
      </button>

      <button
        class="btn primary bottom-action-btn"
        :disabled="!selectedSiteId"
        @click="saveStep3"
      >
        로거/센서 설정 저장
      </button>
    </div>

    <div v-if="showRegisterModal" class="modal-backdrop" @click.self="closeRegisterModal">
      <div class="register-modal">
        <div class="modal-header">
          <div class="modal-title">센서 등록</div>
          <button class="modal-close" @click="closeRegisterModal">×</button>
        </div>

        <div class="modal-body">
          <div class="setting-item">
            <label>센서명</label>
            <input v-model="registerForm.name" class="control" />
          </div>

          <div class="setting-item">
            <label>센서종류</label>
            <select v-model="registerForm.sensorTypeId" class="control" @change="onRegisterTypeChange">
              <option :value="null">- 선택 -</option>
              <option v-for="type in sensorTypeOptions" :key="type.id" :value="type.id">
                {{ type.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="modal-actions">
          <button class="btn secondary" @click="closeRegisterModal">취소</button>
          <button class="btn primary" @click="confirmRegisterSensor">등록</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'

export default {
  name: 'LoggerSensorManage',

  data() {
    return {
      siteList: [],
      selectedSiteId: null,

      loggers: [],
      selectedLoggerKey: null,

      availableSensors: [],
      selectedRawSensorId: null,

      selectedRegisteredKey: null,

      sensorTypeOptions: [],
      sensorTypeFieldMap: {},
      sensorTypeFormulaMap: {},

      showRegisterModal: false,
      registerTargetSensor: null,

      // 추가
      loadingLoggerSensor: false,

      registerForm: {
        name: '',
        sensorTypeId: null,
      },
    }
  },

  computed: {
    selectedLogger() {
      return this.loggers.find(item => item.localKey === this.selectedLoggerKey) || null
    },

    registeredSensors() {
      return this.selectedLogger?.sensors || []
    },

    selectedRegisteredSensor() {
      if (!this.selectedLogger) return null
      return this.registeredSensors.find(item => item.localKey === this.selectedRegisteredKey) || null
    },

    selectedSensorFields() {
      if (!this.selectedRegisteredSensor?.sensorTypeId) return []

      const fields = this.sensorTypeFieldMap[this.selectedRegisteredSensor.sensorTypeId] || []

      return [...fields].sort((a, b) => {
        const aOrder = a.sortOrder ?? a.sort_order ?? 0
        const bOrder = b.sortOrder ?? b.sort_order ?? 0
        return aOrder - bOrder
      })
    },

    selectedSensorCustomFields() {
      return this.selectedSensorFields.filter(field => {
        const key = this.getFieldKey(field)

        return (
          key !== 'install_date' &&
          key !== 'unit' &&
          !this.isThresholdEnabledFieldKey(key)
        )
      })
    },

    availableAxisSensors() {
      return this.availableSensors.filter(sensor => {
        const rawKey = String(sensor.rawMeasureKey || '').toLowerCase()
        return ['amountch0', 'amountch1', 'amountch2'].includes(rawKey)
      })
    },
  },

  async mounted() {
    await Promise.all([
      this.loadSiteList(),
      this.loadSensorTypes(),
    ])

    const routeSiteId = this.$route.query.siteId || this.$route.query.id
    if (routeSiteId) {
      this.selectedSiteId = Number(routeSiteId)
      await this.loadStep3Detail()
    }
  },

  methods: {
    async loadSiteList() {
      try {
        const res = await api.get('/app/site/list', {
          params: {
            deleted: false,
            orderBy: 'siteNameAsc',
          },
        })

        this.siteList = Array.isArray(res?.data?.result)
          ? res.data.result
          : []
      } catch (e) {
        console.error('site list load failed', e)
        this.siteList = []
      }
    },

    async loadSensorTypes() {
      try {
        const res = await api.get('/app/sensorType/list', {
          params: {
            deleted: false,
            orderBy: 'idAsc',
            page: 0,
            size: 1000,
          },
        })

        this.sensorTypeOptions = res.data?.result?.content || res.data?.result || []

        await Promise.all(
          this.sensorTypeOptions.flatMap(type => [
            this.loadSensorTypeFields(type.id),
            this.loadSensorTypeFormulas(type.id),
          ])
        )
      } catch (e) {
        console.error('sensor type list load failed', e)
        this.sensorTypeOptions = []
      }
    },

    async loadSensorTypeFields(sensorTypeId) {
      if (!sensorTypeId) return

      try {
        const res = await api.get('/app/sensorTypeField/list', {
          params: {
            sensorTypeId,
            deleted: false,
            orderBy: 'sortOrderAsc',
            page: 0,
            size: 1000,
          },
        })

        this.sensorTypeFieldMap = {
          ...this.sensorTypeFieldMap,
          [sensorTypeId]: res.data?.result?.content || res.data?.result || [],
        }
      } catch (e) {
        console.error('sensor type field list load failed', e)
        this.sensorTypeFieldMap = {
          ...this.sensorTypeFieldMap,
          [sensorTypeId]: [],
        }
      }
    },

    async loadSensorTypeFormulas(sensorTypeId) {
      if (!sensorTypeId) return

      try {
        const res = await api.get('/app/sensorTypeFormula/list', {
          params: { sensorTypeId },
        })

        this.sensorTypeFormulaMap = {
          ...this.sensorTypeFormulaMap,
          [sensorTypeId]: res.data?.result?.content || res.data?.result || [],
        }
      } catch (e) {
        console.error('sensor type formula list load failed', e)
        this.sensorTypeFormulaMap = {
          ...this.sensorTypeFormulaMap,
          [sensorTypeId]: [],
        }
      }
    },

    async onSiteChange() {
      await this.loadStep3Detail()
    },

    async reloadAll() {
      if (!this.selectedSiteId) return
      await this.loadStep3Detail()
    },

    async loadStep3Detail() {
      if (!this.selectedSiteId) return

      this.selectedLoggerKey = null
      this.selectedRawSensorId = null
      this.selectedRegisteredKey = null
      this.availableSensors = []

      try {
        const res = await api.get('/app/site/step3/detail', {
          params: {
            siteId: this.selectedSiteId,
          },
        })

        const loggerItems = res?.data?.result?.loggerItems || []

        this.loggers = loggerItems.map((item, index) => ({
          ...item,
          localKey: `LOGGER:${item.sensorLoggerSiteId || item.sensorLoggerId || index}`,
          sensors: (item.sensors || [])
            .sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999))
            .map((sensor, sensorIndex) => this.normalizeRegisteredSensor(sensor, sensorIndex)),
        }))

        if (this.loggers.length) {
          await this.selectLogger(this.loggers[0])
        }
      } catch (e) {
        console.error('step3 detail load failed', e)
        this.loggers = []
      }
    },

    normalizeRegisteredSensor(sensor, index = 0) {
      return {
        localKey: `REG:${sensor.sensorSiteId || sensor.sensorId || index}:${Date.now()}:${Math.random()}`,
        sensorSiteId: sensor.sensorSiteId || null,
        sensorId: sensor.sensorId,
        sensorCode: sensor.sensorCode || '',
        serialNumber: sensor.serialNumber || '',
        name: sensor.sensorName || sensor.name || '',
        sensorTypeId: sensor.sensorTypeId ?? null,
        sensorTypeName: sensor.sensorTypeName || '',
        useYn: sensor.useYn ?? true,
        smsSendYn: sensor.smsSendYn ?? false,
        decimalPlaces: sensor.decimalPlaces ?? '',
        unit: sensor.unit || '',
        locationDesc: sensor.locationDesc || '',
        explain: sensor.explain || '',
        chartMin: sensor.chartMin ?? '',
        chartMax: sensor.chartMax ?? '',
        threshold1Main: sensor.threshold1Main ?? '',
        threshold1Sub: sensor.threshold1Sub ?? '',
        threshold2Main: sensor.threshold2Main ?? '',
        threshold2Sub: sensor.threshold2Sub ?? '',
        threshold3Main: sensor.threshold3Main ?? '',
        threshold3Sub: sensor.threshold3Sub ?? '',
        installedAt: sensor.installedAt || '',
        sortOrder: sensor.sortOrder ?? index + 1,
        configValues: {
          ...(sensor.configValues || {}),

          threshold_1_main: sensor?.configValues?.threshold_1_main ?? sensor.threshold1Main ?? '',
          threshold_1_sub: sensor?.configValues?.threshold_1_sub ?? sensor.threshold1Sub ?? '',
          threshold_2_main: sensor?.configValues?.threshold_2_main ?? sensor.threshold2Main ?? '',
          threshold_2_sub: sensor?.configValues?.threshold_2_sub ?? sensor.threshold2Sub ?? '',
          threshold_3_main: sensor?.configValues?.threshold_3_main ?? sensor.threshold3Main ?? '',
          threshold_3_sub: sensor?.configValues?.threshold_3_sub ?? sensor.threshold3Sub ?? '',

          threshold_1_main_enabled: sensor?.configValues?.threshold_1_main_enabled ?? (this.hasConfigValue(sensor?.configValues?.threshold_1_main ?? sensor.threshold1Main) ? 'true' : 'false'),
          threshold_1_sub_enabled: sensor?.configValues?.threshold_1_sub_enabled ?? (this.hasConfigValue(sensor?.configValues?.threshold_1_sub ?? sensor.threshold1Sub) ? 'true' : 'false'),
          threshold_2_main_enabled: sensor?.configValues?.threshold_2_main_enabled ?? (this.hasConfigValue(sensor?.configValues?.threshold_2_main ?? sensor.threshold2Main) ? 'true' : 'false'),
          threshold_2_sub_enabled: sensor?.configValues?.threshold_2_sub_enabled ?? (this.hasConfigValue(sensor?.configValues?.threshold_2_sub ?? sensor.threshold2Sub) ? 'true' : 'false'),
          threshold_3_main_enabled: sensor?.configValues?.threshold_3_main_enabled ?? (this.hasConfigValue(sensor?.configValues?.threshold_3_main ?? sensor.threshold3Main) ? 'true' : 'false'),
          threshold_3_sub_enabled: sensor?.configValues?.threshold_3_sub_enabled ?? (this.hasConfigValue(sensor?.configValues?.threshold_3_sub ?? sensor.threshold3Sub) ? 'true' : 'false'),

          chart_min: sensor?.configValues?.chart_min ?? sensor.chartMin ?? '',
          chart_max: sensor?.configValues?.chart_max ?? sensor.chartMax ?? '',
          unit: sensor?.configValues?.unit ?? sensor.unit ?? '',
          install_location: sensor?.configValues?.install_location ?? sensor.locationDesc ?? '',
          install_date: sensor?.configValues?.install_date ?? '',
          description: sensor?.configValues?.description ?? sensor.explain ?? '',
          formula_1: sensor?.configValues?.formula_1 ?? '',
          formula_2: sensor?.configValues?.formula_2 ?? '',
          formula_3: sensor?.configValues?.formula_3 ?? '',
          formula_4: sensor?.configValues?.formula_4 ?? '',
          formula_5: sensor?.configValues?.formula_5 ?? '',
          formula_6: sensor?.configValues?.formula_6 ?? '',
          connected_sensor_code: sensor?.configValues?.connected_sensor_code ?? '',
        },
      }
    },

    async selectLogger(logger) {
      this.loadingLoggerSensor = true

      // 기존 데이터 먼저 비움
      this.selectedLoggerKey = logger.localKey
      this.selectedRawSensorId = null
      this.selectedRegisteredKey = null
      this.availableSensors = []

      try {
        await this.loadAvailableSensors(logger)

        this.selectedRegisteredKey =
          logger.sensors?.[0]?.localKey || null
      } finally {
        this.loadingLoggerSensor = false
      }
    },

    async loadAvailableSensors(logger) {
      if (!logger?.sensorLoggerId) return

      try {
        const res = await api.get('/app/sensor/list', {
          params: {
            sensorLoggerId: logger.sensorLoggerId,
            useYn: true,
            deleted: false,
            sensorLoggerDeleted: false,
            orderBy: 'createdAtDesc',
            page: 0,
            size: 1000,
          },
        })

        const content = res.data?.result?.content || []

        this.availableSensors = content.map(item => ({
          sensorId: item.id,
          sensorCode: item.code || item.sensorCode || '',
          name: item.name || '',
          serialNumber: item.serialNumber || '',
          rawMeasureKey: item.rawMeasureKey || '',
          sensorTypeId: item.sensorTypeId ?? null,
          sensorTypeName: item.sensorTypeName || '',
          useYn: item.useYn,
          smsSendYn: item.smsSendYn,
          decimalPlaces: item.decimalPlaces,
          unit: item.unit,
          locationDesc: item.locationDesc,
          explain: item.explain,
          chartMin: item.chartMin,
          chartMax: item.chartMax,
          threshold1Main: item.threshold1Main,
          threshold1Sub: item.threshold1Sub,
          threshold2Main: item.threshold2Main,
          threshold2Sub: item.threshold2Sub,
          threshold3Main: item.threshold3Main,
          threshold3Sub: item.threshold3Sub,
        }))
      } catch (e) {
        console.error('sensor list load failed', e)
        this.availableSensors = []
      }
    },

    selectRawSensor(sensor) {
      this.selectedRawSensorId = sensor.sensorId

      const registered = this.findRegisteredByMasterSensorId(sensor.sensorId)
      if (registered) {
        this.selectRegisteredSensor(registered)
      }
    },

    selectRegisteredSensor(sensor) {
      if (!sensor) return
      this.selectedRegisteredKey = sensor.localKey
    },

    isRegistered(rawSensor) {
      return !!this.findRegisteredByMasterSensorId(rawSensor.sensorId)
    },

    findRegisteredByMasterSensorId(sensorId) {
      return this.registeredSensors.find(sensor =>
        Number(sensor.sensorId) === Number(sensorId)
      ) || null
    },

    resolveRegisteredName(rawSensor) {
      const registered = this.findRegisteredByMasterSensorId(rawSensor.sensorId)
      return registered ? (registered.name || registered.sensorCode || '-') : '-'
    },

    openRegisterModal(rawSensor) {
      this.registerTargetSensor = rawSensor
      this.registerForm = {
        name: rawSensor.name || rawSensor.rawMeasureKey || rawSensor.sensorCode || '',
        sensorTypeId: rawSensor.sensorTypeId ?? null,
      }

      this.showRegisterModal = true
    },

    closeRegisterModal() {
      this.showRegisterModal = false
      this.registerTargetSensor = null
      this.registerForm = {
        name: '',
        sensorTypeId: null,
      }
    },

    async onRegisterTypeChange() {
      if (this.registerForm.sensorTypeId) {
        await this.loadSensorTypeFields(this.registerForm.sensorTypeId)
        await this.loadSensorTypeFormulas(this.registerForm.sensorTypeId)
      }
    },

    async confirmRegisterSensor() {
      if (!this.selectedLogger || !this.registerTargetSensor) return

      if (!this.registerForm.name?.trim()) {
        alert('센서명을 입력해주세요.')
        return
      }

      const raw = this.registerTargetSensor
      const type = this.sensorTypeOptions.find(
        item => String(item.id) === String(this.registerForm.sensorTypeId)
      )

      if (type?.id) {
        await this.loadSensorTypeFields(type.id)
        await this.loadSensorTypeFormulas(type.id)
      }

      const next = this.normalizeRegisteredSensor({
        sensorSiteId: null,
        sensorId: raw.sensorId,
        sensorCode: raw.sensorCode || '',
        serialNumber: raw.serialNumber || '',
        sensorName: this.registerForm.name.trim(),
        sensorTypeId: type?.id ?? null,
        sensorTypeName: type?.name || '',
        useYn: true,
        smsSendYn: raw.smsSendYn ?? false,
        decimalPlaces: type?.defaultDecimalPlaces ?? raw.decimalPlaces ?? '',
        unit: type?.defaultUnit ?? raw.unit ?? '',
        chartMin: type?.defaultChartMin ?? raw.chartMin ?? '',
        chartMax: type?.defaultChartMax ?? raw.chartMax ?? '',
        threshold1Main: type?.defaultThreshold1Main ?? raw.threshold1Main ?? '',
        threshold1Sub: type?.defaultThreshold1Sub ?? raw.threshold1Sub ?? '',
        threshold2Main: type?.defaultThreshold2Main ?? raw.threshold2Main ?? '',
        threshold2Sub: type?.defaultThreshold2Sub ?? raw.threshold2Sub ?? '',
        threshold3Main: type?.defaultThreshold3Main ?? raw.threshold3Main ?? '',
        threshold3Sub: type?.defaultThreshold3Sub ?? raw.threshold3Sub ?? '',
        sortOrder: this.selectedLogger.sensors.length + 1,
        configValues: type ? this.getSensorTypeDefaultConfig(type) : {},
      }, this.selectedLogger.sensors.length)

      this.selectedLogger.sensors.push(next)
      this.selectedRegisteredKey = next.localKey

      this.closeRegisterModal()
    },

    removeRegisteredSensor(sensor) {
      if (!this.selectedLogger || !sensor) return

      const ok = window.confirm(`"${sensor.name || sensor.sensorCode}" 센서 등록을 해제하시겠습니까?`)
      if (!ok) return

      this.selectedLogger.sensors = this.selectedLogger.sensors.filter(
        item => item.localKey !== sensor.localKey
      )

      this.selectedRegisteredKey = this.selectedLogger.sensors?.[0]?.localKey || null
    },

    async onSensorTypeChange(sensor) {
      if (!sensor.sensorTypeId) return

      await this.loadSensorTypeFields(sensor.sensorTypeId)
      await this.loadSensorTypeFormulas(sensor.sensorTypeId)

      const type = this.sensorTypeOptions.find(
        item => String(item.id) === String(sensor.sensorTypeId)
      )

      if (!type) return

      sensor.sensorTypeName = type.name
      sensor.configValues = this.getSensorTypeDefaultConfig(type)

      sensor.chartMin = type.defaultChartMin ?? ''
      sensor.chartMax = type.defaultChartMax ?? ''
      sensor.threshold1Main = type.defaultThreshold1Main ?? ''
      sensor.threshold1Sub = type.defaultThreshold1Sub ?? ''
      sensor.threshold2Main = type.defaultThreshold2Main ?? ''
      sensor.threshold2Sub = type.defaultThreshold2Sub ?? ''
      sensor.threshold3Main = type.defaultThreshold3Main ?? ''
      sensor.threshold3Sub = type.defaultThreshold3Sub ?? ''
      sensor.unit = type.defaultUnit || ''
      sensor.decimalPlaces = type.defaultDecimalPlaces ?? ''
    },

    getSensorTypeDefaultConfig(type) {
      if (!type) return {}

      const threshold1Main = type.defaultThreshold1Main ?? ''
      const threshold1Sub = type.defaultThreshold1Sub ?? ''
      const threshold2Main = type.defaultThreshold2Main ?? ''
      const threshold2Sub = type.defaultThreshold2Sub ?? ''
      const threshold3Main = type.defaultThreshold3Main ?? ''
      const threshold3Sub = type.defaultThreshold3Sub ?? ''

      const formulaConfig = this.getSensorTypeDefaultFormulaConfig(type)

      return {
        threshold_1_main: threshold1Main,
        threshold_1_sub: threshold1Sub,
        threshold_2_main: threshold2Main,
        threshold_2_sub: threshold2Sub,
        threshold_3_main: threshold3Main,
        threshold_3_sub: threshold3Sub,

        threshold_1_main_enabled: this.hasConfigValue(threshold1Main) ? 'true' : 'false',
        threshold_1_sub_enabled: this.hasConfigValue(threshold1Sub) ? 'true' : 'false',
        threshold_2_main_enabled: this.hasConfigValue(threshold2Main) ? 'true' : 'false',
        threshold_2_sub_enabled: this.hasConfigValue(threshold2Sub) ? 'true' : 'false',
        threshold_3_main_enabled: this.hasConfigValue(threshold3Main) ? 'true' : 'false',
        threshold_3_sub_enabled: this.hasConfigValue(threshold3Sub) ? 'true' : 'false',

        chart_min: type.defaultChartMin ?? '',
        chart_max: type.defaultChartMax ?? '',
        unit: type.defaultUnit ?? '',
        install_location: '',
        install_date: '',
        description: '',

        formula_1: formulaConfig.formula_1 ?? '',
        formula_2: formulaConfig.formula_2 ?? '',
        formula_3: formulaConfig.formula_3 ?? '',
        formula_4: formulaConfig.formula_4 ?? '',
        formula_5: formulaConfig.formula_5 ?? '',
        formula_6: formulaConfig.formula_6 ?? '',
        connected_sensor_code: '',
      }
    },

    getSensorTypeDefaultFormulaConfig(type) {
      if (!type?.id) return {}

      const formulas = this.sensorTypeFormulaMap[type.id] || []
      const result = {}

      formulas.forEach(item => {
        const stepNo = Number(item.stepNo)
        if (!Number.isFinite(stepNo)) return
        if (stepNo < 1 || stepNo > 6) return

        result[`formula_${stepNo}`] = item.expression || ''
      })

      return result
    },

    getFieldKey(field) {
      return field.fieldKey || field.field_key
    },

    resolveInputType(field) {
      const key = this.getFieldKey(field)

      if (key === 'axis_type') return 'SELECT_AXIS'

      if (
        key === 'axis_1_sensor_id' ||
        key === 'axis_2_sensor_id' ||
        key === 'axis_3_sensor_id'
      ) {
        return 'SELECT_SENSOR'
      }

      return field.inputType || field.input_type || 'INPUT'
    },

    isFullWidthField(field) {
      const key = this.getFieldKey(field)
      return ['install_location', 'description'].includes(key)
    },

    isThresholdValueField(field) {
      const key = this.getFieldKey(field)

      return [
        'threshold_1_main',
        'threshold_1_sub',
        'threshold_2_main',
        'threshold_2_sub',
        'threshold_3_main',
        'threshold_3_sub',
      ].includes(key)
    },

    isThresholdEnabledFieldKey(key) {
      return [
        'threshold_1_main_enabled',
        'threshold_1_sub_enabled',
        'threshold_2_main_enabled',
        'threshold_2_sub_enabled',
        'threshold_3_main_enabled',
        'threshold_3_sub_enabled',
      ].includes(key)
    },

    getThresholdEnabledKey(thresholdKey) {
      return `${thresholdKey}_enabled`
    },

    hasConfigValue(value) {
      return value !== null && value !== undefined && String(value).trim() !== ''
    },

    isTrueValue(value) {
      return value === true || value === 'true' || value === 'Y' || value === '1' || value === 1
    },

    isThresholdEnabled(sensor, thresholdKey) {
      if (!sensor?.configValues) return false

      const enabledKey = this.getThresholdEnabledKey(thresholdKey)
      const enabledValue = sensor.configValues[enabledKey]

      if (this.hasConfigValue(enabledValue)) {
        return this.isTrueValue(enabledValue)
      }

      return this.hasConfigValue(sensor.configValues[thresholdKey])
    },

    setThresholdEnabled(sensor, thresholdKey, checked) {
      if (!sensor.configValues) sensor.configValues = {}

      const enabledKey = this.getThresholdEnabledKey(thresholdKey)
      sensor.configValues[enabledKey] = checked ? 'true' : 'false'
    },

    async saveStep3() {
      if (!this.selectedSiteId) {
        alert('현장을 선택해주세요.')
        return
      }

      const payload = {
        siteId: this.selectedSiteId,
        loggerItems: this.loggers.map((logger, loggerIndex) => ({
          sensorLoggerSiteId: logger.sensorLoggerSiteId || null,
          sensorLoggerId: logger.sensorLoggerId,
          name: logger.name || logger.loggerType || '',
          description: logger.description || '',
          positionX: logger.positionX ?? 0,
          positionY: logger.positionY ?? 0,
          sortOrder: loggerIndex + 1,
          sensors: (logger.sensors || []).map((sensor, sensorIndex) => ({
            sensorSiteId: sensor.sensorSiteId || null,
            sensorId: sensor.sensorId,
            sensorCode: sensor.sensorCode || '',
            serialNumber: sensor.serialNumber || '',
            sortOrder: sensorIndex + 1,
            sensorTypeId: sensor.sensorTypeId || null,
            configValues: sensor.configValues || {},
          })),
        })),
      }

      try {
        await api.post('/app/site/step3/save', payload)
        alert('로거/센서 설정이 저장되었습니다.')
        await this.loadStep3Detail()
      } catch (e) {
        console.error('logger sensor save failed', e)
        alert(e?.response?.data?.message || '저장 실패')
      }
    },
  },
}
</script>

<style scoped>
.logger-sensor-manage {
  height: 100%;
  min-height: 0;
  padding: 18px;
  background: #f6f8fb;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
}

.page-header {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  padding: 18px 20px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.05);
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #111827;
}

.page-subtitle {
  margin-top: 6px;
  font-size: 13px;
  color: #64748b;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.site-select {
  width: 260px;
}

.manage-grid {
  flex: 1;
  min-height: 0;

  display: grid;
  grid-template-columns: 230px minmax(360px, 1fr) 280px 360px;
  gap: 14px;

  overflow: hidden;
}

.panel {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.05);
  overflow: hidden;
}

.panel-title {
  flex-shrink: 0;
  font-size: 17px;
  font-weight: 800;
  color: #111827;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.panel-subtitle {
  flex-shrink: 0;
  margin: 12px 0 6px;
  font-size: 12px;
  color: #64748b;
}

.list-box,
.raw-table-wrapper,
.setting-form {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.setting-form {
  padding-top: 14px;
  padding-right: 8px;
}

.logger-item,
.registered-item {
  width: 100%;
  max-width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 12px;
  border-radius: 10px;
  cursor: pointer;
  box-sizing: border-box;
  white-space: normal;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.logger-item:hover,
.registered-item:hover {
  background: #f8fafc;
}

.logger-item.active,
.registered-item.active {
  background: #e8eef6;
  box-shadow: inset 3px 0 0 #3b82f6;
}

.logger-name,
.registered-name {
  font-size: 14px;
  font-weight: 800;
  color: #111827;
  white-space: normal;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.logger-meta,
.registered-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
  word-break: break-all;
}

.sensor-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  margin-top: 14px;
}

.sensor-table th,
.sensor-table td {
  padding: 11px 8px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  text-align: center;
  vertical-align: middle;
}

.sensor-table th {
  background: #f8fafc;
  font-weight: 800;
}

.sensor-table th:nth-child(1),
.sensor-table td:nth-child(1) {
  width: 120px;
}

.sensor-table th:nth-child(3),
.sensor-table td:nth-child(3) {
  width: 90px;
}

.sensor-table th:nth-child(4),
.sensor-table td:nth-child(4) {
  width: 90px;
}

.sensor-table td {
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  height: 24px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.status-badge.active {
  background: #dcfce7;
  color: #166534;
}

.status-badge.inactive {
  background: #fee2e2;
  color: #991b1b;
}

.registered-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.registered-item > div {
  flex: 1;
  min-width: 0;
}

.remove-btn {
  flex-shrink: 0;
  min-width: 44px;
  border: none;
  border-radius: 8px;
  padding: 6px 8px;
  background: #fee2e2;
  color: #dc2626;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
}

.setting-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 12px;
}

.setting-row.two {
  grid-template-columns: 1fr;
}

.setting-item {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.setting-item label {
  font-size: 13px;
  font-weight: 800;
  color: #374151;
}

.threshold-label {
  display: flex;
  align-items: center;
  gap: 6px;
}

.control {
  width: 100%;
  height: 38px;
  border: 1px solid #d7deea;
  border-radius: 8px;
  padding: 0 10px;
  box-sizing: border-box;
  font-size: 13px;
  background: #fff;
}

.btn {
  border: none;
  border-radius: 9px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.btn.primary {
  background: #e8871a;
  color: #fff;
}

.btn.secondary {
  background: #e2e8f0;
  color: #334155;
}

.btn.xs {
  padding: 6px 10px;
  font-size: 12px;
}

.btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.multi-warning,
.empty-text,
.empty-cell,
.setting-empty,
.loading-empty {
  padding: 24px 12px;
  text-align: center;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.empty-cell {
  height: 120px;
}

.bottom-action-bar {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;

  margin-top: 12px;
  padding: 12px 18px;

  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.05);
}

.bottom-action-btn {
  min-width: 150px;
  height: 42px;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.45);
}

.register-modal {
  width: 460px;
  padding: 24px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.28);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 14px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 18px;
  font-weight: 800;
}

.modal-close {
  border: none;
  background: transparent;
  font-size: 28px;
  cursor: pointer;
  color: #64748b;
}

.modal-body {
  padding: 18px 0;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* 브라우저 확대/폭 부족 시: 센서 설정을 아래로 내리고 전체 페이지 스크롤 허용 */
@media (max-width: 1450px) {
  .logger-sensor-manage {
    height: 100%;
    overflow-y: auto;
  }

  .manage-grid {
    flex: none;
    min-height: auto;
    overflow: visible;

    grid-template-columns: 260px minmax(420px, 1fr) 320px;
    align-items: stretch;
  }

  .setting-panel {
    grid-column: 1 / -1;
    min-height: 460px;
    max-height: 620px;
  }

  .setting-form {
    max-height: 540px;
  }
}

@media (max-height: 760px) {
  .page-header {
    padding: 14px 20px;
    margin-bottom: 10px;
  }

  .page-title {
    font-size: 20px;
  }

  .page-subtitle {
    font-size: 12px;
  }

  .bottom-action-bar {
    margin-top: 8px;
    padding: 8px 18px;
  }

  .bottom-action-btn {
    height: 38px;
  }
}
</style>